/**
 * @format
 */
import { inject, Injectable } from '@angular/core';
import { PlanValidationResult, ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitSelection,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    Education,
    EntityWithRule,
    GradeRaiseAttempt,
    OtmId,
    StudyRight,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { ModalService } from 'sis-common';
import { CustomAttainmentCreditInfoModalComponent } from 'sis-components/attainment/custom-attainment-credit-info-modal/custom-attainment-credit-info-modal.component';
import { SelectCompletionMethodComponent } from '../../plan/select-completion-method/select-completion-method.component';
import {
    StudentCustomStudyDraftModalComponent,
    StudentCustomStudyDraftOptions,
} from '../../plan/student-custom-study-draft-modal/student-custom-study-draft-modal.component';
import { StudentCourseUnitInfoModalComponent } from '../components/course-unit-info-modal/student-course-unit-info-modal/student-course-unit-info-modal.component';
import { StudentModuleInfoModalComponent } from '../components/module-info/student-module-info-modal/student-module-info-modal.component';
import { createCustomStudyDraftModalOpener } from '../components/plan-common/create-custom-study-draft-modal/create-custom-study-draft-modal.component';
import { planStructureEditModalOpener } from '../components/plan-common/plan-structure-edit-modal/plan-structure-edit-modal.component';
import { AttainmentStudentModalService } from './attainment-student-modal-service';

@Injectable({
    providedIn: 'root',
})
export class StudentPlanUiActionsServiceImplementation {
    modalService = inject(ModalService);
    attainmentModalService = inject(AttainmentStudentModalService);

    private planStructureEditModalOpener = planStructureEditModalOpener();
    private createCustomStudyDraftModalOpener = createCustomStudyDraftModalOpener();

    openModuleHandler(
        module: EntityWithRule,
        validatablePlan: ValidatablePlan,
        matchingStudyRight: StudyRight,
        education: Education,
        planValidationResult: PlanValidationResult,
    ) {
        return this.planStructureEditModalOpener({
            module,
            validatablePlan,
            education,
            planValidationResult,
            validatablePlanStudyRight: matchingStudyRight,
        });
    }

    openCustomStudyDraftInfoModalHandler(customStudyDraft: CustomStudyDraft, studentId: OtmId, options: StudentCustomStudyDraftOptions) {
        const customStudyDraftModalValues = { studentId, customStudyDraft, options };
        return this.modalService.open(StudentCustomStudyDraftModalComponent, customStudyDraftModalValues, { size: 'sm' });
    }

    openCustomAttainmentInfoModalHandler(customCourseUnitAttainment: CustomCourseUnitAttainment) {
        return this.modalService.open(CustomAttainmentCreditInfoModalComponent, customCourseUnitAttainment, {
            closeWithOutsideClick: true,
            size: 'lg',
        });
    }

    openCourseUnitInfoModalHandler(courseUnit: CourseUnit, validatablePlan: ValidatablePlan) {
        return this.modalService.open(
            StudentCourseUnitInfoModalComponent,
            {
                courseUnit,
                validatablePlan,
            },
            { closeWithOutsideClick: true, size: 'xl', windowClass: 'brochure-modal' },
        );
    }

    openSelectCompletionMethodModalHandler(
        courseUnit: CourseUnit,
        plannedCourseUnitId: OtmId,
        validatablePlan: ValidatablePlan,
        isGradeRaiseAttempt: boolean,
    ) {
        const courseUnitSelection = validatablePlan.getCourseUnitSelection(plannedCourseUnitId) as CourseUnitSelection;
        const currentAttainmentId = validatablePlan.getCourseUnitAttainment(plannedCourseUnitId)?.id;

        const { assessmentItemSelections, completionMethodId, gradeRaiseAttempt } = this.getCompletionMethodModalParams(
            courseUnit,
            validatablePlan,
            courseUnitSelection,
            isGradeRaiseAttempt,
        );

        return this.modalService.open(
            SelectCompletionMethodComponent,
            {
                assessmentItemSelections,
                currentAttainmentId,
                isGradeRaiseAttempt,
                completionMethodId,
                courseUnit,
                validatablePlan,
                gradeRaiseAttempt,
                plannedCourseUnitId,
            },
            { size: 'sm' },
        );
    }

    private getCompletionMethodModalParams(
        courseUnit: CourseUnit,
        validatablePlan: ValidatablePlan,
        courseUnitSelection: CourseUnitSelection,
        isGradeRaiseAttempt: boolean,
    ) {
        if (courseUnitSelection?.gradeRaiseAttempt) {
            const gradeRaiseAttempt = courseUnitSelection.gradeRaiseAttempt;
            return {
                assessmentItemSelections: gradeRaiseAttempt.selectedAssessmentItemIds,
                completionMethodId: gradeRaiseAttempt.completionMethodId,
                gradeRaiseAttempt,
            };
        }

        return {
            assessmentItemSelections: isGradeRaiseAttempt ? [] : _.map(validatablePlan.getSelectedAssessmentItems(courseUnit), ai => ai.id),
            completionMethodId: isGradeRaiseAttempt ? null : courseUnitSelection?.completionMethodId,
        };
    }

    openOpenAttainmentDetailsModalHandler(attainment: Attainment, gradeRaiseAttempt: GradeRaiseAttempt) {
        return this.attainmentModalService.openAttainmentDetailsModal(attainment, true, false, gradeRaiseAttempt);
    }

    openModuleInfoModalHandler(
        module: EntityWithRule,
        validatablePlan: ValidatablePlan,
        matchingStudyRight: StudyRight,
        planValidationResult: PlanValidationResult,
    ) {
        const isModuleInPlan = validatablePlan?.isModuleInPlan(module.id) || false;
        return this.modalService.open(
            StudentModuleInfoModalComponent,
            {
                module,
                validatablePlan: isModuleInPlan ? validatablePlan : null,
                studyRight: matchingStudyRight,
                planValidationResult: isModuleInPlan ? planValidationResult : null,
            },
            { closeWithOutsideClick: true, size: 'xl', windowClass: 'brochure-modal' },
        );
    }

    openCreateCustomStudyDraftModalHandler(selectedModuleId: OtmId) {
        return this.createCustomStudyDraftModalOpener({ selectedModuleId });
    }
}
